import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type OnboardingStatus =
  | 'draft' | 'submitted' | 'under_review' | 'sent_back' | 'withdrawn'
  | 'document_verification_pending' | 'clarification_required'
  | 'document_verified' | 'technical_evaluation_pending' | 'technical_approved' | 'commercial_evaluation_pending'
  | 'commercial_approved' | 'final_approval_pending' | 'approved' | 'conditional_approval' | 'rejected'
  | 'active' | 'suspended' | 'blacklisted';

export type ReviewDecision = 'verify' | 'approve' | 'reject' | 'conditional' | 'request_clarification' | 'send_back';

export type OperationalStatus = 'active' | 'inactive' | 'suspended' | 'blacklisted' | 'on_hold';
export const OPERATIONAL_STATUSES: OperationalStatus[] = ['active', 'inactive', 'suspended', 'blacklisted', 'on_hold'];

/** Vendor classification options (Part 2). */
export const VENDOR_TYPE_GROUPS: Record<string, string[]> = {
  'Execution Partners': ['Electrical Contractor', 'Civil Contractor', 'EV Charger Installation Partner', 'Testing & Commissioning Partner', 'SLA / Maintenance Partner', 'Breakdown Support Partner'],
  'Infrastructure Partners': ['Fabrication Vendor', 'Canopy Fabrication Vendor', 'Branding & Signage Vendor', 'Earthing Contractor', 'Cabling Contractor'],
  'Material Suppliers': ['OEM / Manufacturer', 'Distributor', 'Trader / Reseller', 'Electrical Material Supplier', 'Civil Material Supplier'],
  'Specialized Services': ['Survey & Liaisoning Partner', 'Network & CCTV Vendor', 'Solar EPC Partner', 'DG / Transformer Vendor', 'Logistics Partner', 'Warehouse Partner'],
  'Professional Services': ['Manpower Provider', 'Project Management Consultant', 'Safety Consultant', 'Quality Audit Partner', 'Other'],
};
export const ALL_VENDOR_TYPES = Object.values(VENDOR_TYPE_GROUPS).flat();

export interface VendorApplication {
  id: number;
  application_code: string;
  user_id: number | null;
  vendor_id: number | null;
  status: OnboardingStatus;
  current_stage: string;
  primary_vendor_type: string | null;
  type_specific_data: Record<string, unknown>;
  legal_name: string;
  trade_name: string | null;
  entity_type: string | null;
  gst_number: string | null;
  pan_number: string | null;
  cin_number: string | null;
  udyam_number: string | null;
  website: string | null;
  year_established: number | null;
  employee_count: number | null;
  payment_terms: string | null;
  credit_period_days: number | null;
  msme_registered: boolean;
  gst_registered: boolean;
  iso_certified: boolean;
  has_safety_policy: boolean;
  has_quality_policy: boolean;
  declaration_accepted: boolean;
  declaration_name: string | null;
  submitted_at: string | null;
  created_at: string | null;
  completed_steps: number[];
  // Source tracking
  source_type: string | null;
  source_project_id: number | null;
  source_region_id: number | null;
  submitted_by: number | null;
  submitted_role: string | null;
  region_id: number | null;
  state_id: number | null;
  // Operational status + user disable
  operational_status: OperationalStatus | null;
  operational_reason: string | null;
  operational_effective_date: string | null;
  operational_remarks: string | null;
  user_disabled: boolean;
  user_disable_reason: string | null;
  cloned_from_id: number | null;
  withdrawn_at: string | null;
  // Enriched (grid) primary-contact fields
  contact_person: string | null;
  contact_email: string | null;
  contact_mobile: string | null;
  // child collections
  secondaryCategories: Array<Record<string, unknown>>;
  contacts: Array<Record<string, unknown>>;
  addresses: Array<Record<string, unknown>>;
  serviceCapabilities: Array<Record<string, unknown>>;
  technicalCapabilities: Array<Record<string, unknown>>;
  oemExperience: Array<Record<string, unknown>>;
  financials: Array<Record<string, unknown>>;
  bankDetails: Array<Record<string, unknown>>;
  projectExperience: Array<Record<string, unknown>>;
  toolsEquipment: Array<Record<string, unknown>>;
  sla: Array<Record<string, unknown>>;
  documents: Array<Record<string, unknown>>;
  // admin-only (absent for vendor)
  document_verification_status?: string | null;
  technical_evaluation_status?: string | null;
  commercial_evaluation_status?: string | null;
  final_status?: string | null;
  internal_remarks?: string | null;
  workflow?: Array<{ id: number; stage: string; stage_order: number; status: string; assigned_role: string | null; remarks: string | null }>;
}

export interface PortalAccess {
  stage: 'onboarding' | 'active';
  status: string;
  applicationId?: number | null;
  vendorId?: number | null;
  vendorType?: string | null;
  group?: string;
  sections: string[];
}

export const vendorOnboardingApi = {
  async me(): Promise<VendorApplication> {
    return (await apiClient.get<ApiSuccess<VendorApplication>>('/vendor-onboarding/me')).data.data;
  },
  async portalAccess(): Promise<PortalAccess> {
    return (await apiClient.get<ApiSuccess<PortalAccess>>('/vendor-onboarding/portal-access')).data.data;
  },
  async get(id: number): Promise<VendorApplication> {
    return (await apiClient.get<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}`)).data.data;
  },
  async saveDraft(id: number, payload: Record<string, unknown>): Promise<VendorApplication> {
    return (await apiClient.put<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/draft`, payload)).data.data;
  },
  async submit(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/submit`, {})).data.data;
  },
  async respondClarification(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/respond-clarification`, {})).data.data;
  },
  async list(params: Record<string, unknown> = {}): Promise<{ data: VendorApplication[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<VendorApplication[]>>('/vendor-onboarding', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async review(id: number, decision: ReviewDecision, remarks?: string): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/review`, { decision, remarks })).data.data;
  },
  async create(payload: Record<string, unknown>): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>('/vendor-onboarding', payload)).data.data;
  },
  async withdraw(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/withdraw`, {})).data.data;
  },
  async remove(id: number): Promise<{ deleted: true }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true }>>(`/vendor-onboarding/${id}`)).data.data;
  },
  async clone(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/clone`, {})).data.data;
  },
  async reopen(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/reopen`, {})).data.data;
  },
  async disableUser(id: number, reason?: string): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/disable-user`, { reason })).data.data;
  },
  async enableUser(id: number): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/enable-user`, {})).data.data;
  },
  async changeOperationalStatus(id: number, body: { status: OperationalStatus; reason?: string; effectiveDate?: string; remarks?: string }): Promise<VendorApplication> {
    return (await apiClient.post<ApiSuccess<VendorApplication>>(`/vendor-onboarding/${id}/operational-status`, body)).data.data;
  },
};

export interface VendorOnboardingDashboard {
  kpis: Record<string, number>;
  charts: Record<string, Array<{ label: string; value: number }>>;
  tables: {
    recentlySubmitted: Array<Record<string, unknown>>;
    pendingApprovals: Array<Record<string, unknown>>;
    expiringDocuments: { expired: unknown[]; within30Days: unknown[]; within60Days: unknown[]; within90Days: unknown[] };
  };
}

export const vendorDashboardApi = {
  async dashboard(): Promise<VendorOnboardingDashboard> {
    return (await apiClient.get<ApiSuccess<VendorOnboardingDashboard>>('/vendor-dashboard')).data.data;
  },
  async recompute(): Promise<{ updated: number }> {
    return (await apiClient.post<ApiSuccess<{ updated: number }>>('/vendor-dashboard/recompute', {})).data.data;
  },
};
